import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, CheckCircle2, FileText, Loader2, Plus, Send, X } from 'lucide-react';
import { closeContactForm } from '../../store/slices/uiSlice';
import { contactService } from '../../api/contactService';
import {
  CONTACT_LIMITS,
  EMPTY_CONTACT,
  FILE_ACCEPT_ATTRIBUTE,
  buildContactFormData,
  contactErrorMessage,
  formatFileSize,
  validateContact,
} from '../../utils/contactForm';

const FIELD_ORDER = ['name', 'email', 'company', 'message', 'file'];

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const inputClass = (hasError) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:outline-none focus:ring-2 disabled:bg-slate-50 disabled:text-slate-500 ${
    hasError ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100' : 'border-slate-200 focus:border-[#008080] focus:ring-[#008080]/15'
  }`;

const FieldLabel = ({ htmlFor, children, optional }) => (
  <div className="flex items-baseline justify-between gap-3 mb-2">
    <label htmlFor={htmlFor} className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
      {children}
    </label>
    {optional && <span className="text-[11px] text-slate-400">optional</span>}
  </div>
);

const FieldError = ({ id, children }) =>
  children ? (
    <p id={id} className="mt-1.5 flex items-center gap-1 text-xs font-medium text-rose-600">
      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
      {children}
    </p>
  ) : null;

/**
 * The Contact Us form, one modal for the whole app. It opens from the header
 * and the footer through the ui slice, sends to POST /api/contact, and stays
 * mounted once opened so closing it does not throw away what was typed.
 */
export const ContactModal = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const open = useSelector((state) => state.ui.isContactOpen);
  const uid = useId();
  const ids = {
    title: `${uid}-title`,
    intro: `${uid}-intro`,
    name: `${uid}-name`,
    email: `${uid}-email`,
    company: `${uid}-company`,
    message: `${uid}-message`,
    file: `${uid}-file`,
  };

  const [hasOpened, setHasOpened] = useState(false);
  const [values, setValues] = useState(EMPTY_CONTACT);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'failed'
  const [failure, setFailure] = useState('');
  const [sentTo, setSentTo] = useState({ name: '', email: '' });

  const panelRef = useRef(null);
  const fieldRefs = useRef({});
  const fileInputRef = useRef(null);

  const close = useCallback(() => dispatch(closeContactForm()), [dispatch]);

  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  // Focus, keyboard and scroll handling for as long as the dialog is open
  useEffect(() => {
    if (!open) return undefined;
    const returnFocusTo = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const frame = requestAnimationFrame(() => {
      (status === 'sent' ? panelRef.current?.querySelector('button') : fieldRefs.current.name)?.focus();
    });

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll(FOCUSABLE)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      if (returnFocusTo && typeof returnFocusTo.focus === 'function') returnFocusTo.focus();
    };
    // status is read once per opening, to pick what to focus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, close]);

  // A finished conversation starts over the next time the form opens
  useEffect(() => {
    if (!open && status === 'sent') setStatus('idle');
  }, [open, status]);

  if (!hasOpened) return null;

  const sending = status === 'sending';

  const update = (field) => (event) => {
    const next = { ...values, [field]: event.target.value };
    setValues(next);
    // Once a field has been flagged, re-check it as the visitor fixes it
    if (errors[field]) setErrors((current) => ({ ...current, [field]: validateContact(next, file)[field] }));
  };

  const chooseFile = (event) => {
    const chosen = event.target.files?.[0] || null;
    setFile(chosen);
    setErrors((current) => ({ ...current, file: validateContact(values, chosen).file }));
  };

  const removeFile = () => {
    setFile(null);
    setErrors((current) => ({ ...current, file: undefined }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;

    const found = validateContact(values, file);
    setErrors(found);
    const firstInvalid = FIELD_ORDER.find((field) => found[field]);
    if (firstInvalid) {
      (firstInvalid === 'file' ? fileInputRef.current : fieldRefs.current[firstInvalid])?.focus();
      return;
    }

    setStatus('sending');
    setFailure('');
    try {
      await contactService.sendMessage(buildContactFormData(values, file, location.pathname));
      setSentTo({ name: values.name.trim().split(/\s+/)[0], email: values.email.trim() });
      setValues(EMPTY_CONTACT);
      removeFile();
      setStatus('sent');
    } catch (error) {
      // Not sent: say so, and keep everything the visitor typed
      setFailure(contactErrorMessage(error));
      setStatus('failed');
    }
  };

  const describedBy = (field) => (errors[field] ? `${ids[field]}-error` : undefined);

  return (
    <div hidden={!open} className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={close} aria-hidden="true" />

      <div className="relative flex min-h-full items-center justify-center p-3 sm:p-6 pointer-events-none">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ids.title}
          aria-describedby={ids.intro}
          className="pointer-events-auto flex w-full max-w-2xl max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-3rem)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 sm:px-8 pt-6 sm:pt-7 pb-5 sm:pb-6">
            <div>
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                <span className="w-1.5 h-1.5 rounded-full bg-[#008080]" aria-hidden="true" />
                Contact us
              </p>
              <h2 id={ids.title} className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                Get in touch
              </h2>
              <p id={ids.intro} className="mt-1.5 text-sm text-slate-500">
                Questions about an IPO, our data or advertising? Send us a note and we&apos;ll reply by email.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close contact form"
              className="flex-shrink-0 w-10 h-10 rounded-full border border-slate-200 bg-white text-slate-500 hover:text-slate-900 hover:border-slate-300 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {status === 'sent' ? (
            <div className="px-5 sm:px-8 py-10 sm:py-12 text-center" role="status">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" aria-hidden="true" />
              <p className="mt-4 text-lg font-extrabold text-slate-900">Message sent</p>
              <p className="mt-1.5 text-sm text-slate-500">
                Thanks{sentTo.name ? `, ${sentTo.name}` : ''}. We&apos;ve received your message and will reply to{' '}
                <span className="font-semibold text-slate-700">{sentTo.email}</span>.
              </p>
              <button
                type="button"
                onClick={close}
                className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#008080] hover:bg-[#006666] text-sm font-bold text-white transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 overflow-y-auto scrollbar-slim px-5 sm:px-8 py-6 space-y-5">
                {status === 'failed' && failure && (
                  <p role="alert" className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    {failure}
                  </p>
                )}

                <div>
                  <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
                  <input
                    ref={(node) => (fieldRefs.current.name = node)}
                    id={ids.name}
                    type="text"
                    autoComplete="name"
                    value={values.name}
                    onChange={update('name')}
                    disabled={sending}
                    maxLength={CONTACT_LIMITS.name + 20}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={describedBy('name')}
                    className={inputClass(errors.name)}
                  />
                  <FieldError id={`${ids.name}-error`}>{errors.name}</FieldError>
                </div>

                <div>
                  <FieldLabel htmlFor={ids.email}>Email</FieldLabel>
                  <input
                    ref={(node) => (fieldRefs.current.email = node)}
                    id={ids.email}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={values.email}
                    onChange={update('email')}
                    disabled={sending}
                    maxLength={CONTACT_LIMITS.email + 20}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy('email')}
                    className={inputClass(errors.email)}
                  />
                  <FieldError id={`${ids.email}-error`}>{errors.email}</FieldError>
                </div>

                <div>
                  <FieldLabel htmlFor={ids.company} optional>
                    Company
                  </FieldLabel>
                  <input
                    ref={(node) => (fieldRefs.current.company = node)}
                    id={ids.company}
                    type="text"
                    autoComplete="organization"
                    value={values.company}
                    onChange={update('company')}
                    disabled={sending}
                    maxLength={CONTACT_LIMITS.company + 20}
                    aria-invalid={Boolean(errors.company)}
                    aria-describedby={describedBy('company')}
                    className={inputClass(errors.company)}
                  />
                  <FieldError id={`${ids.company}-error`}>{errors.company}</FieldError>
                </div>

                <div>
                  <FieldLabel htmlFor={ids.message} optional>
                    What would you like to discuss?
                  </FieldLabel>
                  <textarea
                    ref={(node) => (fieldRefs.current.message = node)}
                    id={ids.message}
                    rows={4}
                    value={values.message}
                    onChange={update('message')}
                    disabled={sending}
                    placeholder="A few lines about your question — an IPO, our data, or advertising with us."
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={[describedBy('message'), `${ids.message}-count`].filter(Boolean).join(' ')}
                    className={`${inputClass(errors.message)} resize-y min-h-[112px]`}
                  />
                  <div className="mt-1.5 flex items-start justify-between gap-3">
                    <FieldError id={`${ids.message}-error`}>{errors.message}</FieldError>
                    <span
                      id={`${ids.message}-count`}
                      className={`ml-auto text-[11px] tabular-nums ${
                        values.message.trim().length > CONTACT_LIMITS.message ? 'text-rose-600 font-bold' : 'text-slate-400'
                      }`}
                    >
                      {values.message.trim().length} / {CONTACT_LIMITS.message}
                    </span>
                  </div>
                </div>

                <div>
                  <FieldLabel htmlFor={ids.file} optional>
                    Attach a file
                  </FieldLabel>
                  <input
                    ref={fileInputRef}
                    id={ids.file}
                    type="file"
                    accept={FILE_ACCEPT_ATTRIBUTE}
                    onChange={chooseFile}
                    disabled={sending}
                    aria-invalid={Boolean(errors.file)}
                    aria-describedby={[describedBy('file'), `${ids.file}-hint`].filter(Boolean).join(' ')}
                    className="peer sr-only"
                  />
                  {file ? (
                    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <FileText className="w-5 h-5 text-[#008080] flex-shrink-0" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-800">{file.name}</span>
                        <span className="block text-[11px] text-slate-500">{formatFileSize(file.size)}</span>
                      </span>
                      <button
                        type="button"
                        onClick={removeFile}
                        disabled={sending}
                        aria-label={`Remove ${file.name}`}
                        className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor={ids.file}
                      className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:border-[#008080] hover:text-[#008080] transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#008080]/30"
                    >
                      <Plus className="w-4 h-4" aria-hidden="true" />
                      Choose a file
                    </label>
                  )}
                  <p id={`${ids.file}-hint`} className="mt-1.5 text-[11px] text-slate-400">
                    PDF, Word, PNG or JPG, up to {formatFileSize(CONTACT_LIMITS.fileBytes)}.
                  </p>
                  <FieldError id={`${ids.file}-error`}>{errors.file}</FieldError>
                </div>

                {/* Honeypot: people never see or reach it; form-filling bots do */}
                <div aria-hidden="true" className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden">
                  <label htmlFor={`${uid}-website`}>Website</label>
                  <input
                    id={`${uid}-website`}
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={values.website}
                    onChange={update('website')}
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/70 px-5 sm:px-8 py-4">
                <p className="text-[11px] text-slate-400">We only use these details to reply to you.</p>
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#008080] hover:bg-[#006666] disabled:opacity-70 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" aria-hidden="true" />
                      Send message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
